"use client"

import { useState, useEffect } from "react"
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import { getDatabase, ref as dbRef, onValue, get, set } from "firebase/database"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { FaInfoCircle, FaFileUpload, FaCheck, FaTimes, FaShareAlt } from "react-icons/fa"
import "./DocumentsUploadPage.css"
import { useCurrentUid } from "../hooks/useCurrentUid";


const DocumentsPage = () => {
  const auth = getAuth()
  const database = getDatabase()
  const storage = getStorage()
  const navigate = useNavigate()
  const location = useLocation()

  const [files, setFiles] = useState({
    gstCopy: { url: null, uploading: false },
    panCard: { url: null, uploading: false },
    workOrder: { url: null, uploading: false },
    purchaseOrder: { url: null, uploading: false },
    partnershipDeed: { url: null, uploading: false },
    cancelCheque: { url: null, uploading: false },
  })

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const userId = useCurrentUid();

  const [userCodes, setUserCodes] = useState([])
  const [selectedCode, setSelectedCode] = useState(null)
  const [selectedCodeDetails, setSelectedCodeDetails] = useState(null)
  const [showOptionalPrompt, setShowOptionalPrompt] = useState(false)
  const [proceedingToRedirect, setProceedingToRedirect] = useState(false)

  const fileTypes = ["image/png", "image/jpeg", "application/pdf"]
  const requiredFiles = ["gstCopy", "panCard", "workOrder", "purchaseOrder", "partnershipDeed"]
  const optionalFiles = ["cancelCheque"]

  // Check authentication and fetch user codes
  useEffect(() => {
    const connectedRef = dbRef(database, ".info/connected")

    if (!navigator.onLine) {
      setError("You are offline. Please check your internet connection.")
      setLoading(false)
      return
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setError("Session expired. Please login again.")
        setLoading(false)
        return
      }

      setError("")
      // setUserId(user.uid)

      // Check Firebase connection
      onValue(connectedRef, (snap) => {
        if (snap.val() === false) {
          setError("Session expired. Please login again.")
          setLoading(false)
        } else {
          setError("")
         
        }
      })
    })

    return () => unsubscribe()
  }, [])

  // Re-fetch codes whenever user switches account
useEffect(() => {
  if (!userId) return;

  setLoading(true);

  // Reset right side UI to empty before loading new account's codes
  setSelectedCode(null);
  setSelectedCodeDetails(null);
  setFiles({
    gstCopy: { url: null, uploading: false },
    panCard: { url: null, uploading: false },
    workOrder: { url: null, uploading: false },
    purchaseOrder: { url: null, uploading: false },
    partnershipDeed: { url: null, uploading: false },
    cancelCheque: { url: null, uploading: false },
  });

  fetchUserCodes(userId);
}, [userId]);

  // Fetch documents when selectedCode changes
  useEffect(() => {
    if (!selectedCode || !userId) return

    // Reset files immediately to show pending state
    setFiles({
      gstCopy: { url: null, uploading: false },
      panCard: { url: null, uploading: false },
      workOrder: { url: null, uploading: false },
      purchaseOrder: { url: null, uploading: false },
      partnershipDeed: { url: null, uploading: false },
      cancelCheque: { url: null, uploading: false },
    })

    let isActive = true

    const fetchDocs = async () => {
      const updatedFiles = {
        gstCopy: { url: null, uploading: false },
        panCard: { url: null, uploading: false },
        workOrder: { url: null, uploading: false },
        purchaseOrder: { url: null, uploading: false },
        partnershipDeed: { url: null, uploading: false },
        cancelCheque: { url: null, uploading: false },
      }

      await Promise.all(
        Object.keys(updatedFiles).map(async (field) => {
          const storageRef = ref(storage, `userDocuments/${userId}/${selectedCode}/${field}`)
          try {
            const fileURL = await getDownloadURL(storageRef)
            updatedFiles[field] = { url: fileURL, uploading: false }
          } catch (err) {
            console.error(`Error fetching document ${field} for code ${selectedCode}:`, err)
          }
        })
      )

      if (isActive) {
        setFiles(updatedFiles)
      }
    }

    fetchDocs()

    return () => {
      isActive = false
    }
  }, [selectedCode, userId, storage])

  const handleShare = async (url, field) => {
    try {
      const fileName = field.replace(/([A-Z])/g, " $1").trim();

      // Use the native share dialog if available
      if (navigator.share) {
        await navigator.share({
          title: `${fileName} Document`,
          text: `Here’s my ${fileName} document from Altrange.`,
          url: url,
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Error sharing file:", error);
      alert("Unable to share file. Please try again.");
    }
  };


  // Auto-set completed when all required and optional are uploaded
  useEffect(() => {
    if (!selectedCode || !selectedCodeDetails || selectedCodeDetails.documentsCompleted) return

    const allRequiredUploaded = requiredFiles.every((field) => files[field].url !== null)
    const allOptionalUploaded = optionalFiles.every((field) => files[field].url !== null)

    if (allRequiredUploaded && allOptionalUploaded) {
      const codeRef = dbRef(database, `new-code/${userId}/codes/${selectedCodeDetails.id}/documentsCompleted`)
      set(codeRef, true)
        .then(() => {
          setUserCodes((prev) =>
            prev.map((c) =>
              c.id === selectedCodeDetails.id ? { ...c, documentsCompleted: true } : c
            )
          )
          setSelectedCodeDetails((prev) => ({ ...prev, documentsCompleted: true }))
        })
        .catch((err) => console.error("Error setting documentsCompleted:", err))
    }
  }, [files, selectedCode, selectedCodeDetails, userId, database])

  // Fetch user codes from Firebase
  const fetchUserCodes = async (uid) => {
    try {
      const codesRef = dbRef(database, `new-code/${uid}/codes`)
      const snapshot = await get(codesRef)

      if (snapshot.exists()) {
        const codesData = snapshot.val()
        const codesArray = Object.entries(codesData).map(([key, value]) => ({
          id: key,
          ...value,
        }))
        setUserCodes(codesArray)

        // Auto-select first code if coming from order form
       if (codesArray.length > 0 && !selectedCode) {

          const codeFromLocation = location.state?.codeId
          const targetCode = codeFromLocation
            ? codesArray.find((code) => code.codeID === codeFromLocation)
            : codesArray[0]

          if (targetCode) {
            handleCodeSelection(targetCode)
          }
        }
      }
      setLoading(false)
    } catch (error) {
      console.error("Error fetching user codes:", error)
      setError("Failed to load your codes.")
      setLoading(false)
    }
  }

  // Handle code selection
  const handleCodeSelection = (code) => {
    setSelectedCode(code.codeID)
    setSelectedCodeDetails(code)
    setShowOptionalPrompt(false)
    setProceedingToRedirect(false)
  }

  // Check if all required documents are uploaded for prompt
  useEffect(() => {
    if (!selectedCode || selectedCodeDetails?.documentsCompleted) return

    const allRequiredUploaded = requiredFiles.every((field) => files[field].url !== null)
    const allOptionalUploaded = optionalFiles.every((field) => files[field].url !== null)

    if (allRequiredUploaded && !allOptionalUploaded && !proceedingToRedirect) {
      setShowOptionalPrompt(true)
    } else {
      setShowOptionalPrompt(false)
    }
  }, [files, proceedingToRedirect, selectedCode, selectedCodeDetails])

  // Handle file upload
  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0]
    if (!file || !selectedCode) return

    if (file.size > 5 * 1024 * 1024) {
      alert("File size must not exceed 5MB!")
      return
    }

    if (!fileTypes.includes(file.type)) {
      alert("Invalid file type! Only images and PDFs are allowed.")
      return
    }

    const storageRef = ref(storage, `userDocuments/${userId}/${selectedCode}/${fieldName}`)
    const uploadTask = uploadBytesResumable(storageRef, file)

    setFiles((prev) => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], uploading: true },
    }))

    uploadTask.on(
      "state_changed",
      () => { },
      (error) => {
        console.error("Upload failed", error)
        setFiles((prev) => ({
          ...prev,
          [fieldName]: { ...prev[fieldName], uploading: false },
        }))
      },
      async () => {
        const fileURL = await getDownloadURL(uploadTask.snapshot.ref)
        setFiles((prev) => ({
          ...prev,
          [fieldName]: { url: fileURL, uploading: false },
        }))
      },
    )
  }

  // Handle optional documents choice
  const handleOptionalChoice = (proceed) => {
    setShowOptionalPrompt(false)

    if (!proceed) {
      const codeRef = dbRef(database, `new-code/${userId}/codes/${selectedCodeDetails.id}/documentsCompleted`)
      set(codeRef, true)
        .then(() => {
          setUserCodes((prev) =>
            prev.map((c) =>
              c.id === selectedCodeDetails.id ? { ...c, documentsCompleted: true } : c
            )
          )
          setSelectedCodeDetails((prev) => ({ ...prev, documentsCompleted: true }))
          setProceedingToRedirect(true)

          setTimeout(() => {
            navigate("/dashboard/product-order", {
              replace: true,
            })
          }, 1000)
        })
        .catch((err) => console.error("Error setting documentsCompleted:", err))
    }
  }

  // Info popup
  const docsInfo = () => {
    alert(
      "To proceed with registration for non-trade cement purchases, please upload the required documents. Acceptable formats include JPG, PNG, and PDF, with a maximum size of 5MB per file.",
    )
  }

  // Get completion status for a code
  const getCodeCompletionStatus = (codeId) => {
    const code = userCodes.find((code) => code.codeID === codeId)
    return !!code?.documentsCompleted
  }

  if (error) {
    return <div className="error-container">{error}</div>
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading your codes...</p>
      </div>
    )
  }

  return (
    <div className="documents-page-container">
      <div className="documents-header">
        <h1 className="documents-title">
          Document Management <FaInfoCircle onClick={docsInfo} className="info-icon" />
        </h1>
        <p className="documents-subtitle">Upload required documents for your registered codes</p>
      </div>

      <div className="documents-main-content">
        {/* Left Sidebar - Code List */}
        <div className="codes-sidebar">
          <div className="codes-header">
            <h3>Your Codes</h3>
            <span className="codes-count">{userCodes.length} codes</span>
          </div>

          <div className="codes-list">
            {userCodes.length === 0 ? (
              <div className="no-codes">
                <p>No codes found</p>
                <Link to="/dashboard/product-order" className="create-code-link">
                  Create your first order
                </Link>
              </div>
            ) : (
              userCodes.map((code) => (
                <div
                  key={code.id}
                  className={`code-item ${selectedCode === code.codeID ? "active" : ""}`}
                  onClick={() => handleCodeSelection(code)}
                >
                  <div className="code-header">
                    <div className="code-id ">
                      <strong>{code.codeID}</strong>
                      {getCodeCompletionStatus(code.codeID) && <FaCheck className="completion-icon" />}
                    </div>
                    <div className="code-brand">{code.brand}</div>
                  </div>

                  <div className="code-details">
                    <div className="code-balance">Balance: ₹{(code.balance || 0).toLocaleString("en-IN")}</div>

                    {code.sites && Object.keys(code.sites).length > 0 && (
                      <div className="code-sites">
                        <small>{Object.keys(code.sites).length} site(s)</small>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Content - Document Upload */}
        <div className="documents-content">
          {!selectedCode ? (
            <div className="no-selection">
              <FaFileUpload className="upload-icon-large" />
              <h3>Select a Code</h3>
              <p>Choose a code from the left sidebar to upload documents</p>
            </div>
          ) : (
            <div className="upload-section">
              <div className="selected-code-info">
                <h3>Documents for Code: {selectedCode}</h3>
                {selectedCodeDetails && (
                  <div className="code-info-card">
                    <div className="info-row">
                      <span className="info-label">Brand:</span>
                      <span className="info-value">{selectedCodeDetails.brand}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Balance:</span>
                      <span className="info-value">₹{(selectedCodeDetails.balance || 0).toLocaleString("en-IN")}</span>
                    </div>
                    {selectedCodeDetails.sites && Object.keys(selectedCodeDetails.sites).length > 0 && (
                      <div className="info-row">
                        <span className="info-label">Sites:</span>
                        <div className="sites-list">
                          {Object.values(selectedCodeDetails.sites).map((site, index) => (
                            <div key={index} className="site-item">
                              {site.address}, {site.state}
                              {site.pincode && `, ${site.pincode}`}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {showOptionalPrompt && (
                <div className="optional-docs-prompt">
                  <div className="prompt-content">
                    <FaCheck className="Check-icon" />
                    <h4>All required documents uploaded!</h4>
                    <p>Do you want to upload the optional documents too?</p>
                    <div className="prompt-actions">
                      <button className="btn-yes" onClick={() => handleOptionalChoice(true)}>
                        Yes, I will upload
                      </button>
                      <button className="btn-no" onClick={() => handleOptionalChoice(false)}>
                        No, proceed
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="documents-grid">
                {Object.keys(files).map((field) => (
                  <div key={field} className="document-card">
                    <div className="document-header">
                      <div className={`status-indicator ${files[field].url ? "completed" : "pending"}`}>
                        {files[field].url ? <FaCheck /> : <FaTimes />}
                      </div>
                      <h4 className="document-name">
                        {field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                        {optionalFiles.includes(field) && <span className="optional-tag">Optional</span>}
                      </h4>
                    </div>

                    <div className="document-body">
                      {files[field].url ? (
                        <div className="file-uploaded">
                          <a
                            href={files[field].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="view-file-btn"
                          >
                            View File
                          </a>

                          <button
                            className="share-btn"
                            onClick={() => handleShare(files[field].url, field)}
                            title="Share document"
                          >
                            <FaShareAlt />
                            Share
                          </button>
                        </div>
                      ) : (
                        <div className="no-file">
                          <span>No file uploaded</span>
                        </div>
                      )}

                      <div className="upload-actions">
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg,.pdf"
                          className="file-input"
                          id={`${field}_${selectedCode}`}
                          onChange={(e) => handleFileUpload(e, field)}
                        />
                        <label htmlFor={`${field}_${selectedCode}`} className="upload-btn">
                          <FaFileUpload />
                          {files[field].url ? "Replace" : "Upload"}
                        </label>
                      </div>

                      {files[field].uploading && (
                        <div className="upload-progress">
                          <div className="progress-bar">
                            <div className="progress-fill"></div>
                          </div>
                          <span>Uploading...</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="upload-notes">
                <p className="file-size-note">
                  <strong>Note:</strong> Each file should not exceed <strong>5MB</strong> in size.
                </p>
                <p className="privacy-note">
                  <strong>Important:</strong> These documents are required to register a new customer code with the
                  company for non-trade cement purchases.{" "}
                  <Link to="/privacy-policy" className="privacy-link">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DocumentsPage